import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import { useAuthentication } from "../../common/infrastructure/hooks/useAuthentication";
import { getCurrentUserRole } from "../../common/infrastructure/helpers/userHelper";
import { OnboardingFlow, OnboardingItemKey, UserRole } from "../../common/enums";
import { useApiQuery } from "../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../common/infrastructure/hooks/useApiMutation";
import {
  IPendingItem,
  onboardingEndpoints,
  onboardingQueryKey,
} from "./api/onboardingApi";
import { tenantFlow } from "./flows/tenantFlow";
import { managerFlow } from "./flows/managerFlow";
import { instructorFlow } from "./flows/instructorFlow";
import { employeeFlow } from "./flows/employeeFlow";
import {
  IOnboardingContext,
  IOnboardingFlow,
  IOnboardingGroup,
  IOnboardingStep,
  IOnboardingSubstep,
} from "./flows/onboardingTypes";

interface IOnboardingProviderValue {
  isActive: boolean;
  isMinimized: boolean;
  activeStepId: OnboardingItemKey | null;
  activeSubstepIndex: number;
  currentSubstep: IOnboardingSubstep | null;
  effectiveSteps: IOnboardingStep[];
  effectiveGroups: IOnboardingGroup[];
  visibleSteps: IOnboardingStep[];
  visibleGroups: IOnboardingGroup[];
  pendingItemKeys: Set<OnboardingItemKey>;
  totalSteps: number;
  completedCount: number;
  activateStep: (stepId: OnboardingItemKey) => void;
  advanceSubstep: () => void;
  completeStep: (stepId: OnboardingItemKey) => void;
  dismissCurrentStep: () => void;
  dismiss: () => void;
  hideForSession: () => void;
  setMinimized: (value: boolean) => void;
  clearActiveStep: () => void;
  closeSidebar: () => void;
  openSidebar: () => void;
  helpStepOverride: OnboardingItemKey | null;
  setHelpStepOverride: (stepId: OnboardingItemKey | null) => void;
}

const OnboardingContext = createContext<IOnboardingProviderValue | null>(null);

export const useOnboarding = () => {
  return useContext(OnboardingContext);
};

interface IOnboardingProviderProps {
  children: React.ReactNode;
}

/** Check if a step's route config matches the current pathname */
export function stepMatchesPath(step: IOnboardingStep, pathname: string): boolean {
  // matchRoutes: prefix-based matching for wizard/sub-pages
  if (step.matchRoutes?.length) {
    if (step.matchRoutes.some((r) => pathname.startsWith(r))) return true;
  }
  // Primary route: exact match
  if (step.route && pathname === step.route) return true;
  return false;
}

/** Resolve a step's `hidden` flag, which may be a static boolean or a context-aware predicate. */
function resolveHidden(
  hidden: IOnboardingStep["hidden"],
  ctx: IOnboardingContext
): boolean {
  return typeof hidden === "function" ? hidden(ctx) : !!hidden;
}

export const OnboardingProvider = ({ children }: IOnboardingProviderProps) => {
  const { isAuthenticated, currentUser } = useAuthentication();
  const {
    selectedStudio,
    setShowSidebar,
    showGroupTypeOptions,
    showServicesTypeOptions,
    showGymTypeOptions,
  } = useAppContext();
  const location = useLocation();

  const [activeStepId, setActiveStepId] = useState<OnboardingItemKey | null>(null);
  const [activeSubstepIndex, setActiveSubstepIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hiddenForSession, setHiddenForSession] = useState(false);
  const [helpStepOverride, setHelpStepOverride] = useState<OnboardingItemKey | null>(null);

  // Block auto-activation after a step ends, until the next route change
  const blockAutoActivateRef = useRef(false);
  // Blocks route orchestrator while get_to_know_planny is about to fire
  const initialActivationPendingRef = useRef(false);
  // Track previous pathname so we can detect actual route changes
  const prevPathnameRef = useRef(location.pathname);
  // Steps dismissed without completing in this session
  const dismissedInSessionRef = useRef(new Set<OnboardingItemKey>());

  const role = currentUser ? getCurrentUserRole(currentUser) : null;
  const isEligible =
    isAuthenticated &&
    role != null &&
    role !== UserRole.User;

  const { data: pendingItems } = useApiQuery<IPendingItem[]>(
    {
      queryKey: onboardingQueryKey,
      endpoint: onboardingEndpoints.getPendingItems,
      enabled: isEligible,
      globalIsLoading: false,
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const completeItemMutation = useApiMutation({ onSuccess: () => {} });

  const rawFlow: IOnboardingFlow | null = useMemo(() => {
    if (role === UserRole.GlobalAdmin || role === UserRole.Tenant) return tenantFlow;
    if (role === UserRole.Employee_Manager) return managerFlow;
    if (role === UserRole.Employee) return employeeFlow;
    if (role === UserRole.Employee_Instructor || role === UserRole.Employee_Instructor_Group_Events) return instructorFlow;
    return null;
  }, [role]);

  const onboardingCtx: IOnboardingContext | null = useMemo(() => {
    if (!selectedStudio) return null;
    return {
      studio: selectedStudio,
      showGroupTypeOptions,
      showServicesTypeOptions,
      showGymTypeOptions,
    };
  }, [
    selectedStudio,
    showGroupTypeOptions,
    showServicesTypeOptions,
    showGymTypeOptions,
  ]);

  const effectiveSteps = useMemo(() => {
    if (!rawFlow || !onboardingCtx) return [];
    return rawFlow.steps.filter(
      (step) => !step.condition || step.condition(onboardingCtx)
    );
  }, [rawFlow, onboardingCtx]);

  const effectiveGroups = useMemo(() => {
    if (!rawFlow) return [];
    const activeGroupIds = new Set(effectiveSteps.map((s) => s.groupId));
    return rawFlow.groups.filter((g) => activeGroupIds.has(g.id));
  }, [rawFlow, effectiveSteps]);

  const visibleSteps = useMemo(() => {
    if (!onboardingCtx) return [];
    return effectiveSteps.filter((s) => !resolveHidden(s.hidden, onboardingCtx));
  }, [effectiveSteps, onboardingCtx]);

  const visibleGroups = useMemo(() => {
    const visibleGroupIds = new Set(visibleSteps.map((s) => s.groupId));
    return effectiveGroups.filter((g) => visibleGroupIds.has(g.id));
  }, [effectiveGroups, visibleSteps]);

  const pendingItemKeys = useMemo(() => {
    if (!pendingItems) return new Set<OnboardingItemKey>();
    return new Set(pendingItems.map((item) => item.itemKey));
  }, [pendingItems]);

  const totalSteps = visibleSteps.length;
  const completedCount = visibleSteps.filter(
    (s) => !pendingItemKeys.has(s.id)
  ).length;

  const isActive =
    isEligible &&
    !hiddenForSession &&
    !!pendingItems &&
    pendingItemKeys.size > 0 &&
    effectiveSteps.length > 0;

  const activeStep = useMemo(() => {
    if (!activeStepId) return null;
    return effectiveSteps.find((s) => s.id === activeStepId) ?? null;
  }, [activeStepId, effectiveSteps]);

  // Substeps may carry their own condition (e.g. the "test user" tip is hidden for
  // shop-only studios), so resolve the effective substeps for the active step.
  const activeStepSubsteps = useMemo<IOnboardingSubstep[] | null>(() => {
    if (!activeStep?.substeps) return null;
    if (!onboardingCtx) return activeStep.substeps;
    return activeStep.substeps.filter(
      (sub) => !sub.condition || sub.condition(onboardingCtx)
    );
  }, [activeStep, onboardingCtx]);

  const currentSubstep: IOnboardingSubstep | null = useMemo(() => {
    if (!activeStepSubsteps || activeStepSubsteps.length === 0) return null;
    return activeStepSubsteps[activeSubstepIndex] ?? null;
  }, [activeStepSubsteps, activeSubstepIndex]);

  // ─── Actions ───

  const activateStep = useCallback((stepId: OnboardingItemKey) => {
    setActiveStepId(stepId);
    setActiveSubstepIndex(0);
  }, []);

  const clearActiveStep = useCallback(() => {
    blockAutoActivateRef.current = true;
    setActiveStepId(null);
    setActiveSubstepIndex(0);
  }, []);

  const doCompleteItem = useCallback(
    (itemKey: OnboardingItemKey) => {
      completeItemMutation.mutate({
        endpoint: onboardingEndpoints.completeItem,
        method: "post",
        payload: { flow: OnboardingFlow.Onboarding, itemKey },
        invalidateKeys: [onboardingQueryKey],
        disableSuccessToast: true,
      });
    },
    [completeItemMutation]
  );

  const advanceSubstep = useCallback(() => {
    if (!activeStep || !activeStepSubsteps || activeStepSubsteps.length === 0) return;

    const nextIndex = activeSubstepIndex + 1;
    if (nextIndex < activeStepSubsteps.length) {
      setActiveSubstepIndex(nextIndex);
    } else {
      // All substeps done — complete the step
      blockAutoActivateRef.current = true;
      doCompleteItem(activeStep.id);
      setActiveStepId(null);
      setActiveSubstepIndex(0);
    }
  }, [activeStep, activeStepSubsteps, activeSubstepIndex, doCompleteItem]);

  const completeStep = useCallback(
    (stepId: OnboardingItemKey) => {
      blockAutoActivateRef.current = true;
      doCompleteItem(stepId);
      setActiveStepId(null);
      setActiveSubstepIndex(0);
    },
    [doCompleteItem]
  );

  const dismissCurrentStep = useCallback(() => {
    if (activeStepId) {
      dismissedInSessionRef.current.add(activeStepId);
      blockAutoActivateRef.current = true;
    }
    setActiveStepId(null);
    setActiveSubstepIndex(0);
  }, [activeStepId]);

  const dismiss = useCallback(() => {
    blockAutoActivateRef.current = true;
    if (pendingItems) {
      for (const item of pendingItems) {
        doCompleteItem(item.itemKey);
      }
    }
    setActiveStepId(null);
    setActiveSubstepIndex(0);
  }, [pendingItems, doCompleteItem]);

  const hideForSession = useCallback(() => {
    setActiveStepId(null);
    setActiveSubstepIndex(0);
    setHiddenForSession(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setShowSidebar(false);
  }, [setShowSidebar]);

  const openSidebar = useCallback(() => {
    setShowSidebar(true);
  }, [setShowSidebar]);

  // ─── Initial Activation ───
  // GetToKnowPlanny fires once on first load if still pending
  const initialActivationDoneRef = useRef(false);

  useEffect(() => {
    if (initialActivationDoneRef.current) return;
    if (!isActive || activeStepId) return;

    if (pendingItemKeys.has(OnboardingItemKey.GetToKnowPlanny)) {
      initialActivationDoneRef.current = true;
      initialActivationPendingRef.current = true;
      const timer = setTimeout(() => {
        initialActivationPendingRef.current = false;
        setActiveStepId(OnboardingItemKey.GetToKnowPlanny);
        setActiveSubstepIndex(0);
      }, 600);
      return () => {
        clearTimeout(timer);
        initialActivationPendingRef.current = false;
      };
    }

    initialActivationDoneRef.current = true;
  }, [isActive, activeStepId, pendingItemKeys]);

  // ─── Route Orchestrator ───
  // On every route change, clear the block and auto-activate the first matching pending step.

  useEffect(() => {
    blockAutoActivateRef.current = false;
  }, [location.pathname]);

  // Dismiss active steps when navigating away.
  // For route-based steps: check if the new path still matches.
  // For component-driven steps (no route/matchRoutes): only dismiss on
  // actual route changes (not on activeStepId changes, which would clear
  // the step the instant it's activated).
  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    // Only run on actual route changes — not when activeStepId changes.
    // Without this guard, clicking a checklist step would dismiss it immediately
    // (before navigation happens) because the step's route doesn't match yet.
    if (prevPath === location.pathname) return;

    if (!activeStepId) return;
    const step = effectiveSteps.find((s) => s.id === activeStepId);
    if (!step) return;

    if (step.route || step.matchRoutes?.length) {
      if (!stepMatchesPath(step, location.pathname)) {
        setActiveStepId(null);
        setActiveSubstepIndex(0);
      }
    } else {
      // Component-driven step: dismiss on any route change
      setActiveStepId(null);
      setActiveSubstepIndex(0);
    }
  }, [activeStepId, location.pathname, effectiveSteps]);

  useEffect(() => {
    if (!isActive || activeStepId) return;
    if (blockAutoActivateRef.current) return;
    if (initialActivationPendingRef.current) return;

    const matchingStep = effectiveSteps.find(
      (s) =>
        pendingItemKeys.has(s.id) &&
        !dismissedInSessionRef.current.has(s.id) &&
        stepMatchesPath(s, location.pathname)
    );

    if (matchingStep) {
      // Activate after a short delay to let the page settle.
      // The overlay handles missing target elements gracefully
      // (centered tooltip, spotlight retries when element appears).
      const timeoutId = setTimeout(() => {
        setActiveStepId(matchingStep.id);
        setActiveSubstepIndex(0);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, isActive, activeStepId, effectiveSteps, pendingItemKeys]);

  // ─── Context value ───

  const value = useMemo<IOnboardingProviderValue>(
    () => ({
      isActive,
      isMinimized,
      activeStepId,
      activeSubstepIndex,
      currentSubstep,
      effectiveSteps,
      effectiveGroups,
      visibleSteps,
      visibleGroups,
      pendingItemKeys,
      totalSteps,
      completedCount,
      activateStep,
      advanceSubstep,
      completeStep,
      dismissCurrentStep,
      dismiss,
      hideForSession,
      setMinimized: setIsMinimized,
      clearActiveStep,
      closeSidebar,
      openSidebar,
      helpStepOverride,
      setHelpStepOverride,
    }),
    [
      isActive,
      isMinimized,
      activeStepId,
      activeSubstepIndex,
      currentSubstep,
      effectiveSteps,
      effectiveGroups,
      visibleSteps,
      visibleGroups,
      pendingItemKeys,
      totalSteps,
      completedCount,
      activateStep,
      advanceSubstep,
      completeStep,
      dismissCurrentStep,
      dismiss,
      hideForSession,
      clearActiveStep,
      closeSidebar,
      openSidebar,
      helpStepOverride,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
